/**
 * Resources API
 * Unified resource management for artifacts, library, and temp files
 */

import apiClient from './client';
import type {
  ResourceListResponse,
  UserResourceSummary,
  SystemResourceStats,
  ResourceTypeStats,
  CleanupResult,
  DeleteAllResult,
  CleanupRequest,
  DeleteAllRequest,
} from '@/types/resources';

/**
 * Admin resources API
 */
export const resourcesApi = {
  // ==========================================================================
  // System-wide Statistics (Admin)
  // ==========================================================================

  /**
   * Get system-wide resource statistics
   */
  getSystemStats: async (
    includeTopUsers: boolean = true,
    topUsersLimit: number = 10
  ): Promise<SystemResourceStats> => {
    const response = await apiClient.get('/resources/stats', {
      params: {
        include_largest_users: includeTopUsers,
        top_users_limit: topUsersLimit,
      },
    });
    return response.data;
  },

  /**
   * Get stats broken down by type
   */
  getStatsByType: async (): Promise<Record<string, ResourceTypeStats>> => {
    const response = await apiClient.get('/resources/stats/by-type');
    return response.data;
  },

  // ==========================================================================
  // Per-User Resources (Admin)
  // ==========================================================================

  /**
   * Get resource summary for a specific user
   */
  getUserSummary: async (userId: string): Promise<UserResourceSummary> => {
    const response = await apiClient.get(`/resources/user/${userId}/summary`);
    return response.data;
  },

  /**
   * List artifacts for a specific user
   */
  getUserArtifacts: async (
    userId: string,
    params?: {
      subtype?: string;
      include_expired?: boolean;
      include_urls?: boolean;
      limit?: number;
      offset?: number;
    }
  ): Promise<ResourceListResponse> => {
    const response = await apiClient.get(`/resources/user/${userId}/artifacts`, {
      params,
    });
    return response.data;
  },

  /**
   * List library files for a specific user
   */
  getUserLibrary: async (
    userId: string,
    params?: {
      category?: string;
      include_urls?: boolean;
      limit?: number;
    }
  ): Promise<ResourceListResponse> => {
    const response = await apiClient.get(`/resources/user/${userId}/library`, {
      params,
    });
    return response.data;
  },

  /**
   * List temp files for a specific user
   */
  getUserTemp: async (
    userId: string,
    params?: {
      include_urls?: boolean;
      limit?: number;
    }
  ): Promise<ResourceListResponse> => {
    const response = await apiClient.get(`/resources/user/${userId}/temp`, {
      params,
    });
    return response.data;
  },

  // ==========================================================================
  // Resource Deletion (Admin)
  // ==========================================================================

  /**
   * Delete a specific resource for a user
   */
  deleteUserResource: async (
    userId: string,
    resourceId: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(
      `/resources/user/${userId}/resource/${resourceId}`
    );
    return response.data;
  },

  /**
   * Delete all resources for a user
   */
  deleteAllUserResources: async (
    userId: string,
    request?: DeleteAllRequest
  ): Promise<DeleteAllResult> => {
    const response = await apiClient.delete(`/resources/user/${userId}/all`, {
      data: request || {},
    });
    return response.data;
  },

  // ==========================================================================
  // Cleanup Operations (Admin)
  // ==========================================================================

  /**
   * Cleanup expired temp files
   */
  cleanupTempFiles: async (request?: CleanupRequest): Promise<CleanupResult> => {
    const response = await apiClient.post('/resources/cleanup/temp', request || {});
    return response.data;
  },

  /**
   * Cleanup expired artifacts
   */
  cleanupExpiredArtifacts: async (params?: {
    artifact_type?: string;
    user_id?: string;
    delete_files?: boolean;
  }): Promise<CleanupResult> => {
    const response = await apiClient.post('/resources/cleanup/expired', null, {
      params,
    });
    return response.data;
  },
};

export default resourcesApi;
